/**
 * API client functions for pet medication operations
 */

import { API_ENDPOINTS } from '../config/api';
import { Medication, MedicationCreate, MedicationUpdate, MedicationStatus } from '../types/medication';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

/**
 * Get all medications for a pet
 */
export async function getPetMedications(
  petId: string,
  status?: MedicationStatus,
  activeOnly: boolean = false
): Promise<Medication[]> {
  const params = new URLSearchParams();
  if (status) params.append('status_filter', status);
  params.append('active_only', activeOnly.toString());

  const url = `${API_ENDPOINTS.MEDICATIONS.BY_PET(petId)}?${params.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch medications' }));
    throw new Error(error.detail || 'Failed to fetch medications');
  }

  return response.json();
}

/**
 * Get a single medication by ID
 */
export async function getMedication(medicationId: string): Promise<Medication> {
  const response = await fetch(API_ENDPOINTS.MEDICATIONS.GET(medicationId), {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch medication' }));
    throw new Error(error.detail || 'Failed to fetch medication');
  }

  return response.json();
}

/**
 * Create a new medication for a pet
 */
export async function createMedication(
  petId: string,
  data: MedicationCreate
): Promise<Medication> {
  const response = await fetch(API_ENDPOINTS.MEDICATIONS.CREATE(petId), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create medication' }));
    throw new Error(error.detail || 'Failed to create medication');
  }

  return response.json();
}

/**
 * Update a medication
 */
export async function updateMedication(
  medicationId: string,
  data: MedicationUpdate
): Promise<Medication> {
  const response = await fetch(API_ENDPOINTS.MEDICATIONS.UPDATE(medicationId), {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update medication' }));
    throw new Error(error.detail || 'Failed to update medication');
  }

  return response.json();
}

/**
 * Delete a medication (hard delete)
 */
export async function deleteMedication(medicationId: string): Promise<void> {
  const response = await fetch(API_ENDPOINTS.MEDICATIONS.DELETE(medicationId), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete medication' }));
    throw new Error(error.detail || 'Failed to delete medication');
  }
}

/**
 * Discontinue a medication (soft delete - sets status to DISCONTINUED)
 */
export async function discontinueMedication(
  medicationId: string,
  notes?: string
): Promise<Medication> {
  const params = new URLSearchParams();
  if (notes) params.append('notes', notes);

  const url = `${API_ENDPOINTS.MEDICATIONS.DISCONTINUE(medicationId)}${params.toString() ? `?${params.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to discontinue medication' }));
    throw new Error(error.detail || 'Failed to discontinue medication');
  }

  return response.json();
}

